import React, { useMemo, useState } from "react";
import Box from "@mui/material/Box";
import Paper from "@mui/material/Paper";
import Typography from "@mui/material/Typography";
import Button from "@mui/material/Button";
import CircularProgress from "@mui/material/CircularProgress";
import AddIcon from "@mui/icons-material/Add";
import { useLocation, useNavigate } from "react-router";

import { useProject } from "../ProjectProvider";
import { useAssetsCollection } from "../../../hooks/capture/useAssetsCollection";
import useAssetsFilter from "../../../hooks/capture/useAssetsFilter";
import useMutation from "../../../hooks/useMutation";
import { CREATE_KIT_MUTATION } from "../../../constants/graphql/mutations";
import KitDetailsModal from "../../TablePro/components/assets/KitDetailsModal";
import { KitAssetsProvider } from "../../../contexts/kit-assets";
import SearchKits from "./SearchKits";
import MoreOptionsMenu from "./MoreOptionsMenu";
import { KitsCollectionProvider } from "../../../contexts/kitsCollection";
import useQueryParams from "../../../hooks/useQueryParams";

export default function Kitting({ title }) {
  const { project } = useProject();
  const navigate = useNavigate();
  const location = useLocation();
  const queryParams = useQueryParams();
  const [selected, setSelected] = useState(null);
  const selectedKitId = queryParams.get("kitDetailsId");
  const baseVariables = {
    projectId: project?.id,
    order: { item_id: "desc" },
  };
  const { filter, search, setSearch, limit } = useAssetsFilter(baseVariables, "Kit");
  const [{ data, loading, refetch }, { onLoadMore, fetchTotalIds }] = useAssetsCollection(filter);
  const [createKit, { loading: creating }] = useMutation(CREATE_KIT_MUTATION);
  const collection = data?.assetsCollection?.collection || [];

  const updateModalParams = kitId => {
    const params = new URLSearchParams(location.search);

    if (kitId) {
      params.set("kitDetailsId", String(kitId));
    } else {
      params.delete("kitDetailsId");
      params.delete("showKitQuantityCalculation");
    }

    navigate({ search: params.toString() }, { replace: true });
  };

  const handleSelect = kit => {
    setSelected(kit);
    updateModalParams(kit?.id);
  };

  const handleCloseSelected = () => {
    setSelected(null);
    updateModalParams(null);
  };

  const handleAddNewKit = async () => {
    if (!project?.id) return;

    return createKit({
      variables: { projectId: project.id, input: {} },
      onSuccess: async ({ kit }) => {
        await refetch({ fetchPolicy: "network-only" });
        handleSelect(kit);
      },
    });
  };

  const queryVariables = { page: 1, limit, projectId: project?.id };
  const selectedEntity = useMemo(() => {
    if (!selectedKitId) return null;

    const matchingKit = collection.find(asset => String(asset.id) === String(selectedKitId));
    if (matchingKit) return matchingKit;
    if (String(selected?.id) === String(selectedKitId)) return selected;

    return { id: selectedKitId };
  }, [collection, selected, selectedKitId]);

  return (
    <KitsCollectionProvider variables={queryVariables}>
      <Paper sx={{ display: "flex", flexDirection: "column", flex: 1, minHeight: 0 }}>
        {title}
        <Box px={3} pb={2} pt={1} display="flex" flexDirection="column" flex={1} minHeight={0}>
          <Box display="flex" flexDirection="column" flex={1} minHeight={0}>
            <Box display="flex" justifyContent="center" py={1}>
              <Box
                component="img"
                borderRadius={3}
                maxWidth={140}
                maxHeight={110}
                src="/kitting-graphic.webp"
              />
            </Box>
            {loading && !data ? (
              <Box
                display="flex"
                flexDirection="column"
                alignItems="center"
                justifyContent="center"
                minHeight={200}
              >
                <CircularProgress />
              </Box>
            ) : !search && collection.length === 0 ? (
              <Box
                display="flex"
                flexDirection="column"
                alignItems="center"
                justifyContent="center"
              >
                <Typography align="center" mb={1} maxWidth={450}>
                  Use our kitting feature to bundle separate items into a kit. We'll then calculate
                  how many whole kits you can assemble.
                </Typography>
                <Button
                  variant="outlined"
                  startIcon={<AddIcon />}
                  onClick={handleAddNewKit}
                  disabled={creating}
                  sx={{ my: 2 }}
                >
                  Add New Kit
                </Button>
              </Box>
            ) : (
              <>
                <Box display="flex" justifyContent="center">
                  <Typography align="center" variant="body1" noWrap>
                    If you'd like to add more kits, use the "More Options" button
                  </Typography>
                </Box>
                <SearchKits
                  project={project}
                  data={data}
                  loading={loading || creating}
                  onLoadMore={onLoadMore}
                  search={search}
                  setSearch={setSearch}
                  limit={limit}
                  setSelected={handleSelect}
                  refetch={refetch}
                  fetchTotalIds={fetchTotalIds}
                  headerActions={
                    <MoreOptionsMenu onAddNewKit={handleAddNewKit} creating={loading || creating} />
                  }
                />
              </>
            )}
          </Box>
        </Box>
        {selectedEntity && (
          <KitAssetsProvider id={selectedEntity.id}>
            <KitDetailsModal
              projectId={project?.id}
              entity={selectedEntity}
              onClose={handleCloseSelected}
            />
          </KitAssetsProvider>
        )}
      </Paper>
    </KitsCollectionProvider>
  );
}
